"use client";

import { c as _c } from "react/compiler-runtime";
import type { ReactNode, Ref } from "react";
import { Checkbox as AriaCheckbox, type CheckboxProps as AriaCheckboxProps } from "react-aria-components";
import { cx } from "@/utils/cx";
export interface CheckboxBaseProps {
  size?: "sm" | "md";
  className?: string;
  isFocusVisible?: boolean;
  isSelected?: boolean;
  isDisabled?: boolean;
  isIndeterminate?: boolean;
}
export const CheckboxBase = t0 => {
  const $ = _c(24);
  const {
    className,
    isSelected,
    isDisabled,
    isIndeterminate,
    size: t1,
    isFocusVisible: t2
  } = t0;
  const size = t1 === undefined ? "sm" : t1;
  const isFocusVisible = t2 === undefined ? false : t2;
  const t3 = size === "md" && "size-5 rounded-md";
  const t4 = (isSelected || isIndeterminate) && "bg-brand-solid ring-bg-brand-solid";
  const t5 = isDisabled && "cursor-not-allowed bg-disabled_subtle ring-disabled";
  const t6 = isFocusVisible && "outline-2 outline-offset-2 outline-focus-ring";
  let t7;
  if ($[0] !== className || $[1] !== t3 || $[2] !== t4 || $[3] !== t5 || $[4] !== t6) {
    t7 = cx("flex size-4 shrink-0 cursor-pointer appearance-none items-center justify-center rounded bg-primary ring-1 ring-primary ring-inset", t3, t4, t5, t6, className);
    $[0] = className;
    $[1] = t3;
    $[2] = t4;
    $[3] = t5;
    $[4] = t6;
    $[5] = t7;
  } else {
    t7 = $[5];
  }
  const t8 = size === "md" && "size-3.5";
  const t9 = isIndeterminate && "opacity-100";
  const t10 = isDisabled && "text-fg-disabled_subtle";
  let t11;
  if ($[6] !== t10 || $[7] !== t8 || $[8] !== t9) {
    t11 = cx("pointer-events-none absolute h-3 w-2.5 text-fg-white opacity-0 transition-inherit-all", t8, t9, t10);
    $[6] = t10;
    $[7] = t8;
    $[8] = t9;
    $[9] = t11;
  } else {
    t11 = $[9];
  }
  let t12;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <path d="M2.91675 7H11.0834" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />;
    $[10] = t12;
  } else {
    t12 = $[10];
  }
  let t13;
  if ($[11] !== t11) {
    t13 = <svg aria-hidden="true" viewBox="0 0 14 14" fill="none" className={t11}>{t12}</svg>;
    $[11] = t11;
    $[12] = t13;
  } else {
    t13 = $[12];
  }
  const t14 = size === "md" && "size-3.5";
  const t15 = isSelected && !isIndeterminate && "opacity-100";
  const t16 = isDisabled && "text-fg-disabled_subtle";
  let t17;
  if ($[13] !== t14 || $[14] !== t15 || $[15] !== t16) {
    t17 = cx("pointer-events-none absolute size-3 text-fg-white opacity-0 transition-inherit-all", t14, t15, t16);
    $[13] = t14;
    $[14] = t15;
    $[15] = t16;
    $[16] = t17;
  } else {
    t17 = $[16];
  }
  let t18;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <path d="M11.6666 3.5L5.24992 9.91667L2.33325 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />;
    $[17] = t18;
  } else {
    t18 = $[17];
  }
  let t19;
  if ($[18] !== t17) {
    t19 = <svg aria-hidden="true" viewBox="0 0 14 14" fill="none" className={t17}>{t18}</svg>;
    $[18] = t17;
    $[19] = t19;
  } else {
    t19 = $[19];
  }
  let t20;
  if ($[20] !== t13 || $[21] !== t19 || $[22] !== t7) {
    t20 = <div className={t7}>{t13}{t19}</div>;
    $[20] = t13;
    $[21] = t19;
    $[22] = t7;
    $[23] = t20;
  } else {
    t20 = $[23];
  }
  return t20;
};
CheckboxBase.displayName = "CheckboxBase";
interface CheckboxProps extends AriaCheckboxProps {
  ref?: Ref<HTMLLabelElement>;
  size?: "sm" | "md";
  label?: ReactNode;
  hint?: ReactNode;
}
export const Checkbox = t0 => {
  const $ = _c(18);
  let ariaCheckboxProps;
  let className;
  let hint;
  let label;
  let t1;
  if ($[0] !== t0) {
    ({
      label,
      hint,
      size: t1,
      className,
      ...ariaCheckboxProps
    } = t0);
    $[0] = t0;
    $[1] = ariaCheckboxProps;
    $[2] = className;
    $[3] = hint;
    $[4] = label;
    $[5] = t1;
  } else {
    ariaCheckboxProps = $[1];
    className = $[2];
    hint = $[3];
    label = $[4];
    t1 = $[5];
  }
  const size = t1 === undefined ? "sm" : t1;
  let t2;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      sm: {
        root: "gap-2",
        textWrapper: "",
        label: "text-sm font-medium",
        hint: "text-sm"
      },
      md: {
        root: "gap-3",
        textWrapper: "gap-0.5",
        label: "text-md font-medium",
        hint: "text-md"
      }
    };
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const sizes = t2;
  let t3;
  if ($[7] !== className || $[8] !== size) {
    t3 = state => cx("flex items-start", state.isDisabled && "cursor-not-allowed", sizes[size].root, typeof className === "function" ? className(state) : className);
    $[7] = className;
    $[8] = size;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== hint || $[11] !== label || $[12] !== size) {
    t4 = t5 => {
      const {
        isSelected,
        isIndeterminate,
        isDisabled,
        isFocusVisible
      } = t5;
      return <><CheckboxBase size={size} isSelected={isSelected} isIndeterminate={isIndeterminate} isDisabled={isDisabled} isFocusVisible={isFocusVisible} className={label || hint ? "mt-0.5" : ""} />{(label || hint) && <div className={cx("inline-flex flex-col", sizes[size].textWrapper)}>{label && <p className={cx("text-secondary select-none", sizes[size].label)}>{label}</p>}{hint && <span role="button" tabIndex={0} className={cx("text-tertiary", sizes[size].hint)} onClick={_temp} onKeyDown={_temp2}>{hint}</span>}</div>}</>;
    };
    $[10] = hint;
    $[11] = label;
    $[12] = size;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  let t5;
  if ($[14] !== ariaCheckboxProps || $[15] !== t3 || $[16] !== t4) {
    t5 = <AriaCheckbox {...ariaCheckboxProps} className={t3}>{t4}</AriaCheckbox>;
    $[14] = ariaCheckboxProps;
    $[15] = t3;
    $[16] = t4;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  return t5;
};
Checkbox.displayName = "Checkbox";
function _temp(e) {
  return e.stopPropagation();
}
function _temp2(e_0) {
  return e_0.stopPropagation();
}